import type { Geofence } from "@gpx-spa/geo-domain";
import { decodeGoogleRoutesEncodedPolyline } from "@gpx-spa/geo-adapters";
import type {
  RoutePathOption,
  RoutePlan,
  RouteSegmentPlan,
} from "@gpx-spa/geo-domain";
import type {
  LearnedRouteEndpoint,
  LearnedRouteLegMetaDto,
  LearnedRoutePlanDto,
  LearnedRouteRequestBody,
} from "@gpx-spa/queries/src/getAccountLearnedRoutes";
import type { RoutingStrategy } from "@/components/shipment/form/utils/shipment-form-routing";
import { getStopRoutingCoordinates } from "@/components/shipment/form/utils/shipment-form-stop-coords";
import type { ShipmentFormStopRow } from "@/stores/useShipmentFormStore";
import type { LatLng } from "@/components/shipment/form/utils/build-routing-coordinates";
import { COMPOSER_STOP_RADIUS_DEFAULT_M } from "@/components/shipment/form/domain/constants";

export const LEARNED_MAX_ALTERNATIVES_PER_SEGMENT = 3;

function circleRadiusM(radiusM: number): number {
  const rounded = Math.round(radiusM);
  if (!Number.isFinite(rounded)) return 1;
  return Math.min(100000, Math.max(1, rounded));
}

export function shipmentStopToLearnedEndpoint(
  stop: ShipmentFormStopRow,
): LearnedRouteEndpoint | null {
  const geofence: Geofence | null = stop.geofence;
  if (geofence != null && typeof geofence.id === "number" && geofence.id > 0) {
    return { type: "geofence", geofence_id: geofence.id };
  }
  const coords = getStopRoutingCoordinates(stop);
  if (!coords) return null;
  return {
    type: "circle",
    center: { lat: coords.lat, lng: coords.lng },
    radius_m: circleRadiusM(stop.radiusM),
  };
}

function trackerCircleEndpoint(position: LatLng): LearnedRouteEndpoint {
  return {
    type: "circle",
    center: { lat: position.lat, lng: position.lng },
    radius_m: circleRadiusM(COMPOSER_STOP_RADIUS_DEFAULT_M),
  };
}

export function buildLearnedRouteRequest(input: {
  strategy: RoutingStrategy;
  stops: readonly ShipmentFormStopRow[];
  trackerPosition: LatLng | null;
}): LearnedRouteRequestBody | null {
  if (input.strategy === "none") return null;

  if (input.strategy === "trackerToDestination") {
    if (!input.trackerPosition || input.stops.length < 2) return null;
    const destination = shipmentStopToLearnedEndpoint(
      input.stops[input.stops.length - 1]!,
    );
    if (!destination) return null;
    return {
      origin: trackerCircleEndpoint(input.trackerPosition),
      intermediates: [],
      destination,
      travel_mode: "DRIVE",
      max_alternatives_per_segment: LEARNED_MAX_ALTERNATIVES_PER_SEGMENT,
    };
  }

  if (input.stops.length < 2) return null;
  const endpoints: LearnedRouteEndpoint[] = [];
  for (const stop of input.stops) {
    const endpoint = shipmentStopToLearnedEndpoint(stop);
    if (!endpoint) return null;
    endpoints.push(endpoint);
  }
  if (endpoints.length < 2) return null;
  return {
    origin: endpoints[0]!,
    intermediates: endpoints.slice(1, -1),
    destination: endpoints[endpoints.length - 1]!,
    travel_mode: "DRIVE",
    max_alternatives_per_segment: LEARNED_MAX_ALTERNATIVES_PER_SEGMENT,
  };
}

function metaFromLearnedLeg(
  meta: LearnedRouteLegMetaDto | undefined,
): RoutePathOption["meta"] {
  const next: NonNullable<RoutePathOption["meta"]> = {
    provider:
      typeof meta?.provider === "string" && meta.provider.trim()
        ? meta.provider.trim()
        : "learned",
  };
  if (
    typeof meta?.distance_m === "number" &&
    Number.isFinite(meta.distance_m)
  ) {
    next.distance_m = meta.distance_m;
  }
  if (
    typeof meta?.duration_s === "number" &&
    Number.isFinite(meta.duration_s)
  ) {
    next.duration_s = meta.duration_s;
  }
  if (
    typeof meta?.sample_count === "number" &&
    Number.isFinite(meta.sample_count)
  ) {
    next.sample_count = meta.sample_count;
  }
  return next;
}

function pathFromLearnedLeg(
  leg:
    | { polyline?: { encodedPolyline?: string }; meta?: LearnedRouteLegMetaDto }
    | undefined,
  index: number,
): RoutePathOption | null {
  const encodedPolyline = leg?.polyline?.encodedPolyline;
  if (typeof encodedPolyline !== "string" || !encodedPolyline.trim())
    return null;
  const coordinates = decodeGoogleRoutesEncodedPolyline(encodedPolyline);
  if (coordinates.length < 2) return null;
  return {
    id: `learned_${index}`,
    coordinates,
    meta: metaFromLearnedLeg(leg?.meta),
  };
}

export function routePlanFromLearnedResponse(
  data: LearnedRoutePlanDto | null | undefined,
): RoutePlan {
  const routes = data?.routes ?? [];
  const segments: RouteSegmentPlan[] = routes.map((route, hop) => {
    const paths: RoutePathOption[] = [];
    for (const leg of route.legs ?? []) {
      const path = pathFromLearnedLeg(leg, paths.length);
      if (path) paths.push(path);
    }
    return {
      sequence: hop,
      fromStopIndex: hop,
      toStopIndex: hop + 1,
      paths,
    };
  });
  return {
    segments,
    meta: { provider: "learned" },
  };
}
